import { useCallback, useSyncExternalStore } from 'react';

const STORAGE_KEY = 'onyx-theme';

function systemTheme() {
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

function readSaved() {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

// Module-level store shared by every useTheme() call site, so a toggle in
// one component (e.g. ThemeToggle) is reflected instantly everywhere else
// that reads the theme (e.g. HeroFan's screenshot set, App.js's Toaster
// theme) — a per-call-site useState would leave those other components
// stale until something unrelated forced them to re-render.
let theme = document.documentElement.getAttribute('data-theme') || readSaved() || systemTheme();
const listeners = new Set();

function setTheme(next) {
  theme = next;
  document.documentElement.setAttribute('data-theme', next);
  listeners.forEach((l) => l());
}

function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot() {
  return theme;
}

let mqListenerAttached = false;
function ensureSystemListener() {
  if (mqListenerAttached) return;
  mqListenerAttached = true;
  const mq = window.matchMedia('(prefers-color-scheme: light)');
  // A genuine OS-level theme change always wins, even over a previous
  // manual ThemeToggle choice — clearing the saved override so the site
  // keeps live-tracking the system afterward.
  mq.addEventListener('change', (e) => {
    const next = e.matches ? 'light' : 'dark';
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore write failures (private mode, storage disabled, etc.)
    }
    setTheme(next);
  });
}
ensureSystemListener();

export function useTheme() {
  const current = useSyncExternalStore(subscribe, getSnapshot);

  const toggle = useCallback(() => {
    const next = theme === 'light' ? 'dark' : 'light';
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // ignore write failures (private mode, storage disabled, etc.)
    }
    setTheme(next);
  }, []);

  return { theme: current, toggle };
}
